import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
    TextField, Typography, Paper
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { useContext, useEffect, useState } from "react";
import { CountryType, CreateCountryType } from "types/CountryType";
import { VatZoneType } from "types/VatZoneType";
import { getCountries, createCountry, updateCountry, deleteCountry } from "util/network/Countries";
import { getVatZones } from "util/network/VatZones";

const DOMESTIC_ZONE_NUMBER = 1;

const emptyForm: CreateCountryType = {
    code: "",
    name: "",
    enabled: true,
    vat_zone_id: undefined,
};

type SortColumn = "code" | "name" | "enabled" | "vat_zone";

const CountriesManagement: React.FC = () => {
    const { token } = useContext(AppStateContext);
    const { addToast } = useToast();

    const [countries, setCountries] = useState<CountryType[]>([]);
    const [vatZones, setVatZones] = useState<VatZoneType[]>([]);
    const [loading, setLoading] = useState(true);
    const [editCountry, setEditCountry] = useState<CountryType | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<CountryType | null>(null);
    const [form, setForm] = useState<CreateCountryType>(emptyForm);
    const [saving, setSaving] = useState(false);
    const [sortBy, setSortBy] = useState<SortColumn>("code");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    const domesticZone = vatZones.find(z => z.economics_zone_number === DOMESTIC_ZONE_NUMBER);

    const fetchAll = async () => {
        try {
            const data = await getCountries(token);
            setCountries(data);
        } catch {
            addToast({ type: "error", message: "Failed to fetch countries" });
        } finally {
            setLoading(false);
        }
        try {
            const zones = await getVatZones(token);
            setVatZones(zones);
        } catch {
            addToast({ type: "error", message: "Failed to fetch VAT zones" });
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openCreate = () => {
        setEditCountry(null);
        setForm({ ...emptyForm, vat_zone_id: domesticZone?.id });
        setDialogOpen(true);
    };

    const openEdit = (c: CountryType) => {
        setEditCountry(c);
        setForm({ code: c.code, name: c.name, enabled: c.enabled, vat_zone_id: c.vat_zone_id });
        setDialogOpen(true);
    };

    const openDelete = (c: CountryType) => {
        setDeleteTarget(c);
        setDeleteDialogOpen(true);
    };

    const handleSave = async () => {
        const code = form.code.trim().toUpperCase();
        const name = form.name.trim();
        if (!code || !name) {
            addToast({ type: "error", message: "Code and name are required" });
            return;
        }
        if (!form.vat_zone_id) {
            addToast({ type: "error", message: "VAT zone is required" });
            return;
        }
        setSaving(true);
        const payload = { ...form, code, name };
        try {
            if (editCountry) {
                await updateCountry(token, editCountry.id, payload);
                addToast({ type: "success", message: "Country updated" });
            } else {
                await createCountry(token, payload);
                addToast({ type: "success", message: "Country created" });
            }
            setDialogOpen(false);
            await fetchAll();
        } catch {
            addToast({ type: "error", message: editCountry ? "Failed to update country" : "Failed to create country" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setSaving(true);
        try {
            await deleteCountry(token, deleteTarget.id);
            addToast({ type: "success", message: "Country deleted" });
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
            await fetchAll();
        } catch {
            addToast({ type: "error", message: "Failed to delete country" });
        } finally {
            setSaving(false);
        }
    };

    const handleSort = (col: SortColumn) => {
        if (sortBy === col) {
            setSortDir(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortBy(col);
            setSortDir("asc");
        }
    };

    const sortedCountries = [...countries].sort((a, b) => {
        let cmp: number;
        if (sortBy === "vat_zone") {
            const av = a.vat_zone?.economics_zone_number ?? -Infinity;
            const bv = b.vat_zone?.economics_zone_number ?? -Infinity;
            cmp = av - bv;
        } else {
            const av = a[sortBy];
            const bv = b[sortBy];
            if (typeof av === "boolean") {
                cmp = (av === bv) ? 0 : av ? 1 : -1;
            } else {
                cmp = String(av).localeCompare(String(bv));
            }
        }
        return sortDir === "asc" ? cmp : -cmp;
    });

    // Always include the zone the country currently points at, even if it's
    // disabled or missing from the fetched list (e.g. the zone fetch failed) —
    // editing a country must not silently move it off its current zone.
    const currentZone = editCountry?.vat_zone;
    const vatZoneOptionsById = new Map<number, VatZoneType>();
    vatZones.forEach(z => vatZoneOptionsById.set(z.id, z));
    if (currentZone) vatZoneOptionsById.set(currentZone.id, currentZone);

    const vatZoneOptions = Array.from(vatZoneOptionsById.values())
        .filter(z => z.enabled || z.id === form.vat_zone_id)
        .sort((a, b) => a.economics_zone_number - b.economics_zone_number);

    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Countries</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Add Country
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sortDirection={sortBy === "code" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "code"} direction={sortBy === "code" ? sortDir : "asc"} onClick={() => handleSort("code")}>Code</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === "name" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "name"} direction={sortBy === "name" ? sortDir : "asc"} onClick={() => handleSort("name")}>Name</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === "vat_zone" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "vat_zone"} direction={sortBy === "vat_zone" ? sortDir : "asc"} onClick={() => handleSort("vat_zone")}>VAT Zone</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === "enabled" ? sortDir : false}>
                                <TableSortLabel active={sortBy === "enabled"} direction={sortBy === "enabled" ? sortDir : "asc"} onClick={() => handleSort("enabled")}>Enabled</TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedCountries.map(c => (
                            <TableRow key={c.id}>
                                <TableCell>{c.code}</TableCell>
                                <TableCell>{c.name}</TableCell>
                                <TableCell>{c.vat_zone ? `${c.vat_zone.economics_zone_number} — ${c.vat_zone.name}` : "—"}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={c.enabled ? "Enabled" : "Disabled"}
                                        color={c.enabled ? "success" : "default"}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => openDelete(c)}><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {countries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No countries found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{editCountry ? "Edit Country" : "Create Country"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            label="Code (ISO 3166-1 alpha-2)"
                            size="small"
                            fullWidth
                            value={form.code}
                            onChange={e => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                            inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Name"
                            size="small"
                            fullWidth
                            value={form.name}
                            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                            <InputLabel id="vat-zone-select-label">VAT Zone</InputLabel>
                            <Select
                                labelId="vat-zone-select-label"
                                label="VAT Zone"
                                value={form.vat_zone_id ?? ""}
                                onChange={e => setForm(prev => ({ ...prev, vat_zone_id: Number(e.target.value) }))}
                            >
                                {vatZoneOptions.map(z => (
                                    <MenuItem key={z.id} value={z.id}>
                                        {`${z.economics_zone_number} — ${z.name}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.enabled ?? true}
                                    onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
                                />
                            }
                            label="Enabled"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : editCountry ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Country</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete "{deleteTarget?.name}" ({deleteTarget?.code})?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CountriesManagement;
